'use client';

import { FC } from 'react';
import type { RichTextField } from '@prismicio/client';
import { Content, asText, isFilled } from '@prismicio/client';
import { PrismicRichText, SliceComponentProps } from '@prismicio/react';
import { RevealOnScroll } from '@/components/common/RevealOnScroll';

type RedesignSlice = Extract<
  Content.ProblemHighlightGridSlice,
  { variation: 'redesign' }
>;

export type ProblemHighlightGridRedesignProps =
  SliceComponentProps<RedesignSlice>;

const TITLE_CLASS =
  'w-full text-balance break-words text-[28px] font-medium leading-[1.2] md:text-[42px]';

const BODY_CLASS =
  'w-full text-[16px] font-light leading-[1.6] text-[#041118] md:text-[18px]';

const CARD_SHELL =
  'flex min-h-[100px] items-center rounded-lg bg-[#f3f3f3] px-4 py-2';

const CARD_ACCENT = 'flex flex-1 items-start border-l border-[#4b0fea] pl-3';

const SUBTITLE_SERIALIZERS = {
  paragraph: ({ children }: { children: React.ReactNode }) => (
    <p className="[&:not(:first-child)]:mt-4">{children}</p>
  ),
  strong: ({ children }: { children: React.ReactNode }) => (
    <strong className="font-semibold">{children}</strong>
  ),
  hyperlink: ({
    children,
    node,
  }: {
    children: React.ReactNode;
    node: { data: { url?: string } };
  }) => (
    <a
      href={node.data.url ?? '#'}
      className="text-[#4b0fea] underline"
      target="_blank"
      rel="noopener noreferrer"
    >
      {children}
    </a>
  ),
};

const CLOSING_SERIALIZERS = {
  paragraph: ({ children }: { children: React.ReactNode }) => (
    <p className="[&:not(:first-child)]:mt-4">{children}</p>
  ),
  strong: ({ children }: { children: React.ReactNode }) => (
    <strong className="font-bold">{children}</strong>
  ),
  em: ({ children }: { children: React.ReactNode }) => (
    <em className="italic">{children}</em>
  ),
  hyperlink: ({
    children,
    node,
  }: {
    children: React.ReactNode;
    node: { data: { url?: string } };
  }) => (
    <a
      href={node.data.url ?? '#'}
      className="text-[#4b0fea] underline"
      target="_blank"
      rel="noopener noreferrer"
    >
      {children}
    </a>
  ),
};

/**
 * Shared reader for the closing field. Runtime data may still be a plain
 * string until the CMS side of the field migration lands; return an empty
 * RichTextField shape in that case so the isFilled guard skips the render
 * without ever handing a non-array to PrismicRichText.
 */
function readClosing(raw: unknown): RichTextField {
  if (Array.isArray(raw)) {
    return raw as RichTextField;
  }
  return [] as unknown as RichTextField;
}

function BodyCopy({
  subtitle,
  closing,
}: {
  subtitle: RedesignSlice['primary']['subtitle'];
  closing?: RichTextField;
}) {
  const hasSubtitle = isFilled.richText(subtitle);
  const hasClosing = closing ? isFilled.richText(closing) : false;
  if (!hasSubtitle && !hasClosing) {
    return null;
  }

  return (
    <div className={BODY_CLASS}>
      {hasSubtitle ? (
        <PrismicRichText field={subtitle} components={SUBTITLE_SERIALIZERS} />
      ) : null}
      {hasClosing && closing ? (
        <div className={hasSubtitle ? 'mt-4' : undefined}>
          <PrismicRichText field={closing} components={CLOSING_SERIALIZERS} />
        </div>
      ) : null}
    </div>
  );
}

type RedesignPrimaryExtras = {
  closing?: unknown;
  hide_bottom_divider?: boolean;
};

const ProblemHighlightGridRedesign: FC<ProblemHighlightGridRedesignProps> = ({
  slice,
}) => {
  const {
    title,
    subheadline,
    statements,
    cards,
    subtitle,
    remove_upper_margin,
  } = slice.primary;

  const extras = slice.primary as unknown as RedesignPrimaryExtras;
  const closing = readClosing(extras.closing);
  const hideBottomDivider = Boolean(extras.hide_bottom_divider);

  const hasTitle = isFilled.richText(title);
  const hasSubheadline = isFilled.richText(subheadline);

  const statementItems = (statements ?? []).filter((item) => item.statement);
  const cardItems = (cards ?? []).filter(
    (item) => item.card_title || item.card_description,
  );
  const hasStatements = statementItems.length > 0;
  const hasCards = cardItems.length > 0;
  const hasClosing = isFilled.richText(closing);

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className={`bg-white px-5 md:px-[30px] ${
        remove_upper_margin ? 'pt-0' : 'pt-[70px]'
      }`}
    >
      <div
        className={`mx-auto flex w-full max-w-[1280px] flex-col items-start gap-8 pb-[70px] md:gap-9 ${
          hideBottomDivider ? '' : 'border-b border-[#e5e5e5]'
        }`}
      >
        {(hasTitle || hasSubheadline) && (
          <RevealOnScroll className="w-full">
            <h2 className={TITLE_CLASS}>
              {hasTitle && <span className="text-[#aaa]">{asText(title)}</span>}
              {hasTitle && hasSubheadline && <br />}
              {hasSubheadline && (
                <span className="text-[#041118]">{asText(subheadline)}</span>
              )}
            </h2>
          </RevealOnScroll>
        )}

        {hasCards && <BodyCopy subtitle={subtitle} />}

        {hasCards && (
          <RevealOnScroll
            delay={200}
            className="grid w-full grid-cols-1 gap-4 md:grid-cols-3 md:gap-6"
          >
            {cardItems.map((item, index) => (
              <div
                key={`card-${index}`}
                className={`flex min-h-[100px] rounded-lg bg-[#f3f3f3] px-4 py-2 ${
                  item.card_description ? 'items-start' : 'items-center'
                }`}
              >
                <div
                  className={
                    item.card_description
                      ? 'flex flex-1 flex-col gap-3 py-2'
                      : 'flex flex-1 flex-col gap-1 py-2'
                  }
                >
                  {item.card_title ? (
                    // When the card also carries body copy, the title steps up
                    // to the Figma H4 spec (24px / semibold / 140%) and the
                    // gap between title and copy widens to `gap-3`. Cards
                    // without copy keep the original compact 18px / medium
                    // treatment and `gap-1` so single-line cards look
                    // identical to prod.
                    <p
                      className={
                        item.card_description
                          ? 'border-l border-[#4b0fea] pl-3 text-[24px] font-semibold leading-[1.4] text-[#041118]'
                          : 'border-l border-[#4b0fea] pl-3 text-[18px] font-medium leading-[1.4] text-[#041118]'
                      }
                    >
                      {item.card_title}
                    </p>
                  ) : null}
                  {item.card_description ? (
                    <p className="pl-3 text-[16px] font-light leading-[1.5] text-[#041118]">
                      {item.card_description}
                    </p>
                  ) : null}
                </div>
              </div>
            ))}
          </RevealOnScroll>
        )}

        {hasCards && hasClosing ? (
          <div className={BODY_CLASS}>
            <PrismicRichText field={closing} components={CLOSING_SERIALIZERS} />
          </div>
        ) : null}

        {hasStatements && !hasCards && (
          <>
            <RevealOnScroll
              delay={200}
              className="grid w-full grid-cols-1 gap-4 md:grid-cols-3 md:gap-6"
            >
              {statementItems.map((item, index) => (
                <div key={`statement-${index}`} className={CARD_SHELL}>
                  <div className={`${CARD_ACCENT} items-center`}>
                    <span className="text-[18px] font-medium leading-[1.5] text-[#041118]">
                      {item.statement}
                    </span>
                  </div>
                </div>
              ))}
            </RevealOnScroll>
            <BodyCopy subtitle={subtitle} closing={closing} />
          </>
        )}

        {!hasCards && !hasStatements && (
          <BodyCopy subtitle={subtitle} closing={closing} />
        )}
      </div>
    </section>
  );
};

export default ProblemHighlightGridRedesign;
